import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Phone, MessageCircle, CheckCircle, Clock, Shield, Award, MapPin, User, Calendar } from 'lucide-react';
import { contactInfo } from '../data/services';
import { useState, useEffect } from 'react';
import { useServices } from '../context/ServiceContext';
import axios from 'axios';
import { BookingModal } from '../components/BookingModal';
import { ServiceCard } from '../components/ServiceCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { services, loading: loadingServices } = useServices();
  const service = services.find((s) => s.id === serviceId);
  const [servicePersons, setServicePersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ agents_online: 'true' });

  useEffect(() => {
    // Track page visit
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/analytics/track`, {
          page: 'service_detail',
          service_id: serviceId
        });
      } catch (error) {
        console.error('Failed to track visit:', error);
      }
    };

    // Fetch site settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };

    if (serviceId) {
      trackVisit();
      fetchSettings();
    }
  }, [serviceId]);

  if (loadingServices && services.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20" data-testid="service-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Service Not Found</h2>
          <Link to="/" className="text-blue-500 hover:underline">
            Go back to Home
          </Link>
        </div>
      </div>
    );
  }

  const benefits = [
    { icon: Clock, text: 'Quick Response Time' },
    { icon: Shield, text: 'Guaranteed Service' },
    { icon: Award, text: 'Trained Professionals' },
  ];

  return (
    <main className="min-h-screen pt-20 bg-zinc-50" data-testid="service-detail-page">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-500 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={service.image}
                alt={service.name}
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
              
              {/* Rating Badge */}
              <div className="absolute top-6 right-6 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                <Star size={18} className="text-yellow-500 fill-yellow-500" />
                <span className="font-bold text-slate-800">{service.rating}</span>
                <span className="text-sm text-slate-500">Rating</span>
              </div>

              {/* Category Badge */}
              <div className="absolute bottom-6 left-6">
                <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                  {service.category}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Title */}
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4 font-['Oswald']">
                {service.name}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>

            {/* Benefits */}
            <div className="grid sm:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 text-center"
                >
                  <benefit.icon size={24} className="text-blue-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">{benefit.text}</p>
                </div>
              ))}
            </div>

            {/* What's Included */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-slate-800 mb-4 font-['Oswald']">What's Included</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle size={18} className="text-green-500" />
                  Professional service delivery
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle size={18} className="text-green-500" />
                  Quality materials and equipment
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle size={18} className="text-green-500" />
                  Post-service cleanup
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle size={18} className="text-green-500" />
                  Service guarantee
                </li>
              </ul>
            </div>

            {/* CTA Buttons & Badges */}
            <div className="space-y-4">
              {!services.some(s => s.parent_id === service.id) && (
                <>
                  <h3 className="text-lg font-bold text-slate-800 font-['Oswald']">Book This Service</h3>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px] flex flex-col gap-2">
                      <button
                        onClick={() => setIsBookingModalOpen(true)}
                        className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-full font-semibold transition-colors"
                        data-testid="service-book-slot-btn"
                      >
                        <Calendar size={22} />
                        Book a Slot
                      </button>
                    </div>
                    <a
                        href={`https://wa.me/${contactInfo.whatsapp}?text=Hi, I would like to book ${service.name}. Please provide more details.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-[200px] flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-4 px-6 rounded-full font-semibold transition-colors"
                        data-testid="service-whatsapp-btn"
                      >
                        <MessageCircle size={22} />
                        WhatsApp Us
                      </a>
                      <a
                        href={`tel:+91${contactInfo.phone}`}
                        className="flex-1 min-w-[200px] flex items-center justify-center gap-3 bg-blue-500 hover:bg-blue-600 text-white py-4 px-6 rounded-full font-semibold transition-colors"
                        data-testid="service-call-btn"
                      >
                        <Phone size={22} />
                        Call to Book
                      </a>
                  </div>
                </>
              )}
              
              {/* Agent Status & Payment Badge */}
              <div className="mt-2 flex items-center flex-wrap gap-3">
                <div className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-bold shadow-sm transition-all ${
                  (service?.is_active === 't' || service?.is_active === true)
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${(service?.is_active === 't' || service?.is_active === true) ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  {(service?.is_active === 't' || service?.is_active === true) ? 'Technicians are active' : 'Technicians are offline'}
                </div>
                <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-orange-50 border border-orange-200 text-orange-600 text-sm font-bold shadow-sm">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-400" />
                  No online Payments
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Full-width Sub-services Section (using ServicesSection Bento Look) */}
        {services.some(s => s.parent_id === service.id) && (
          <div className="mt-20 w-full relative z-10">
            <h2 className="text-3xl font-bold text-slate-800 mb-10 font-['Oswald'] text-center">
              Our {service.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {services.filter(s => s.parent_id === service.id).map((sub, index) => (
                <ServiceCard key={sub.id} service={sub} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Other Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <h2 className="text-2xl font-bold text-slate-800 mb-8 font-['Oswald']">Other Services You May Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services
              .filter((s) => s.id !== service.id)
              .slice(0, 4)
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/service/${s.id}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`related-service-${s.id}`}
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-semibold text-slate-800 group-hover:text-blue-500 transition-colors line-clamp-1">
                      {s.name}
                    </h4>
                    <div className="flex items-center gap-1 mt-1">
                      <Star size={12} className="text-yellow-500 fill-yellow-500" />
                      <span className="text-xs text-slate-500">{s.rating}</span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </motion.div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        serviceId={serviceId}
        serviceName={service.name}
      />
    </main>
  );
}
